/**
 * Mirrors goal pillar options and per-pillar goal keys/labels from
 * `goal.component.ts` (`pillarOptions` + `goalSections`). Keep in sync when goals change.
 *
 * `GOAL_FIELD_COMPONENT_BY_KEY` / `GOAL_FIELD_UNIT_BY_KEY` mirror `getComponentName` /
 * `getUnitForComponent` in `goal.component.ts` (field key → API component / display unit).
 */
export type GoalPillarKey = 'nutrition' | 'sleep' | 'activity' | 'glucose';

export const GOAL_PILLAR_OPTIONS: Array<{ label: string; value: GoalPillarKey }> = [
  { label: 'Nutrition Pillar', value: 'nutrition' },
  { label: 'Sleep Pillar', value: 'sleep' },
  { label: 'Activity Pillar', value: 'activity' },
  { label: 'Glucose Pillar', value: 'glucose' },
];

export type WishGoalFieldOption = { key: string; label: string };

/** Field key (e.g. nutritionScore) → backend component name. Sync with goal.component.ts getComponentName. */
export const GOAL_FIELD_COMPONENT_BY_KEY: Record<string, string> = {
  calorieConsumption: 'CALORIES_NEEDED',
  proteinIntake: 'PROTEINS_CALORIES_NEEDED',
  carbohydrateIntake: 'CARBS_CALORIES_NEEDED',
  fatIntake: 'FATS_CALORIES_NEEDED',
  mealsPerDay: 'MEALS_PER_DAY',
  stepsCount: 'STEPS',
  activeCaloriesBurnt: 'MOVEMENT_CALORIES_BURN',
  vo2MaxPercentile: 'VO2MAX',
  workoutFrequency: 'WORKOUT_FREQUENCY',
  metMinutes: 'TARGET_MET',
  muscleQuantity: 'MUSCLE_QUANTITY_PERCENT',
  muscleMass: 'MUSCLE_MASS',
  weeklyMiles: 'WEEKLY_MILES',
  sleepDuration: 'TOTAL_SLEEP_DURATION',
  bedTime: 'BED_TIME',
  deepSleepHours: 'DEEP_SLEEP_HOURS',
  timeInRange: 'TIME_IN_RANGE',
  timeInTightRange: 'TIME_IN_TIGHT_RANGE',
  targetBMI: 'TARGET_BMI',
  hba1c: 'HBA1C',
  hba1cDuration: 'HBA1C_TO_ACHIEVE_DURATION',
  fastingGlucose: 'FASTING_GLUCOSE',
  weightToLose: 'WEIGHT_TO_LOSE',
  weightToLoseDuration: 'WEIGHT_TO_LOSE_DURATION',
  sleepScore: 'SLEEP_SCORE',
  readinessScore: 'READINESS_SCORE',
  nutritionScore: 'NUTRITION_SCORE',
  activityScore: 'ACTIVITY_SCORE',
  glucoseScore: 'GLUCOSE_SCORE',
};

/** Field key → unit label (value-only input; unit shown beside field). Sync with goal.component.ts getUnitForComponent. */
export const GOAL_FIELD_UNIT_BY_KEY: Record<string, string> = {
  calorieConsumption: 'kcal',
  proteinIntake: 'g',
  carbohydrateIntake: 'g',
  fatIntake: 'g',
  mealsPerDay: 'meals',
  stepsCount: 'steps',
  activeCaloriesBurnt: 'kcal',
  vo2MaxPercentile: 'mL/kg/min',
  workoutFrequency: 'days/week',
  metMinutes: ' ',
  muscleQuantity: '%',
  muscleMass: 'lb',
  weeklyMiles: 'miles/week',
  sleepDuration: 'hours',
  bedTime: '',
  deepSleepHours: 'hours',
  timeInRange: '%',
  timeInTightRange: '%',
  targetBMI: '',
  hba1c: '%',
  hba1cDuration: 'days',
  fastingGlucose: 'mg/dL',
  weightToLose: 'lb',
  weightToLoseDuration: 'days',
  sleepScore: '',
  readinessScore: '',
  nutritionScore: '',
  activityScore: '',
  glucoseScore: '',
};

export function wishGoalFieldComponent(fieldKey: string | null | undefined): string | undefined {
  if (!fieldKey) return undefined;
  return GOAL_FIELD_COMPONENT_BY_KEY[fieldKey];
}

export function wishGoalFieldUnit(fieldKey: string | null | undefined): string {
  if (!fieldKey) return '';
  return GOAL_FIELD_UNIT_BY_KEY[fieldKey] ?? '';
}

/** Bed Time goal value options — sync with `goal.component.ts` `bedTimeOptions`. */
export const BED_TIME_OPTIONS: readonly string[] = [
  '08:00 PM',
  '08:30 PM',
  '09:00 PM',
  '09:30 PM',
  '10:00 PM',
  '10:30 PM',
  '11:00 PM',
  '11:30 PM',
  '12:00 AM',
  '12:30 AM',
  '01:00 AM',
] as const;

/** Sleep pillar + Bed Time goal uses a select for goal value (string sent to API). */
export function isWishBedTimeGoal(
  pillar: GoalPillarKey | null | undefined,
  fieldKey: string | null | undefined
): boolean {
  return pillar === 'sleep' && fieldKey === 'bedTime';
}

/** Fasting Glucose goal uses a free-text input (range string like "80-120" sent to API). */
export function isWishFastingGlucoseGoal(
  fieldKey: string | null | undefined
): boolean {
  return fieldKey === 'fastingGlucose';
}

export const GOALS_BY_PILLAR: Record<GoalPillarKey, WishGoalFieldOption[]> = {
  nutrition: [
    { key: 'nutritionScore', label: 'Nutrition Score' },
    { key: 'calorieConsumption', label: 'Calories Consumption (kcal)' },
    { key: 'mealsPerDay', label: 'Number of Meals per Day' },
    { key: 'carbohydrateIntake', label: 'Carbohydrate Intake (g)' },
    { key: 'proteinIntake', label: 'Protein Intake (g)' },
    { key: 'fatIntake', label: 'Fat Intake (g)' },
  ],
  activity: [
    { key: 'activityScore', label: 'Activity Score' },
    { key: 'stepsCount', label: 'Steps Count' },
    { key: 'activeCaloriesBurnt', label: 'Active Calories Burned (kcal)' },
    { key: 'vo2MaxPercentile', label: 'VO₂Max (mL/kg/min)' },
    { key: 'workoutFrequency', label: 'Workout Frequency (days/week)' },
    { key: 'metMinutes', label: 'MET (Metabolic Equivalent of Task)' },
    { key: 'muscleQuantity', label: 'Muscle Quantity (%)' },
    { key: 'muscleMass', label: 'Muscle Mass (lb)' },
    { key: 'weeklyMiles', label: 'Weekly Miles (miles/week)' },
    { key: 'weightToLose', label: 'Weight to Lose (lb)' },
    { key: 'weightToLoseDuration', label: 'Weight to Lose Duration (days)' },
    { key: 'targetBMI', label: 'Target BMI' },
  ],
  sleep: [
    { key: 'sleepScore', label: 'Sleep Score' },
    { key: 'readinessScore', label: 'Readiness Score' },
    { key: 'sleepDuration', label: 'Total Sleep Duration (hrs)' },
    { key: 'bedTime', label: 'Bed Time' },
    { key: 'deepSleepHours', label: 'Deep Sleep Hours' },
  ],
  glucose: [
    { key: 'glucoseScore', label: 'Glucose Score' },
    { key: 'timeInRange', label: 'Time in Range (%)' },
    { key: 'timeInTightRange', label: 'Time in Tight Range (%)' },
    { key: 'hba1c', label: 'HBA1C' },
    { key: 'hba1cDuration', label: 'HBA1C to Achieve Duration (days)' },
    { key: 'fastingGlucose', label: 'Fasting Glucose Range' },
  ],
};
